import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Target, 
  Eye, 
  Heart,
  Users,
  Globe,
  Award,
  TrendingUp,
  CheckCircle
} from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We strive for excellence in every order we fulfill, ensuring the highest quality standards.'
    },
    {
      icon: Heart,
      title: 'Integrity',
      description: 'We build trust through transparency, honesty, and reliable service delivery.'
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'We see ourselves as an extension of your team, invested in your success.'
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'We continuously improve our processes and adopt new technologies to serve you better.'
    }
  ];

  const stats = [
    { value: '2019', label: 'Founded' },
    { value: '50+', label: 'Team Members' },
    { value: '100K+', label: 'Products Handled' },
    { value: '5,000+', label: 'Happy Clients' },
  ];

  const milestones = [
    { year: '2019', title: 'Company Founded', description: 'Started operations in Lafayette, Indiana with a vision to simplify ecommerce fulfillment.' },
    { year: '2020', title: 'Expanded Services', description: 'Added Print on Demand capabilities and expanded warehouse capacity.' },
    { year: '2021', title: 'Global Reach', description: 'Established partnerships with international shipping carriers and opened global warehousing.' },
    { year: '2022', title: 'Tech Integration', description: 'Launched advanced inventory management and real-time tracking systems.' },
    { year: '2023', title: 'TikTok Partnership', description: 'Became a certified TikTok Shop fulfillment partner.' },
    { year: '2024', title: 'Scaling Excellence', description: 'Reached milestone of 10 million orders fulfilled with 99.5% accuracy rate.' },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-green-600 font-semibold text-sm tracking-wider uppercase mb-3 block">
                About Us
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Chivo'] font-bold text-gray-900 mb-6" data-testid="about-hero-title">
                Your Trusted Path to{' '}
                <span className="text-green-600">Fulfillment Excellence</span>
              </h1>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8">
                Black Mesa Mercantile LLC is a full-service ecommerce fulfillment company 
                dedicated to helping online businesses scale efficiently. Based in Lafayette, 
                Indiana, we provide comprehensive Print on Demand, Dropshipping, and Warehousing 
                solutions to sellers worldwide.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/services"
                  data-testid="about-cta-services"
                  className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-700 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
                >
                  Our Services
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="about-cta-contact"
                  className="inline-flex items-center gap-2 bg-white text-gray-800 px-6 py-3 rounded-full font-semibold border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-300"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Black Mesa Mercantile team"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -z-10 w-full h-full bg-green-500 opacity-10 rounded-2xl top-4 left-4" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 bg-green-600">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
                data-testid={`about-stat-${index}`}
              >
                <div className="text-3xl md:text-4xl font-['Chivo'] font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-green-100 text-sm md:text-base">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gray-50 rounded-2xl p-8 md:p-10"
            >
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <Target size={28} className="text-green-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-['Chivo'] font-bold text-gray-900 mb-4">
                Our Mission
              </h2>
              <p className="text-gray-600 leading-relaxed">
                To empower ecommerce entrepreneurs by providing reliable, scalable, and cost-effective 
                fulfillment solutions that enable them to focus on growing their businesses while we 
                handle the logistics.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-gray-50 rounded-2xl p-8 md:p-10"
            >
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <Eye size={28} className="text-green-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-['Chivo'] font-bold text-gray-900 mb-4">
                Our Vision
              </h2>
              <p className="text-gray-600 leading-relaxed">
                To become the most trusted fulfillment partner for ecommerce businesses worldwide, 
                known for our commitment to excellence, innovation, and customer success.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-28 bg-gray-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <span className="text-green-600 font-semibold text-sm tracking-wider uppercase mb-3 block">
              What Drives Us
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-['Chivo'] font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg">
              These principles guide everything we do and define who we are as a company.
            </p>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-green-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                data-testid={`value-card-${index}`}
              >
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-5">
                  <value.icon size={24} className="text-green-600" />
                </div>
                <h3 className="text-xl font-['Chivo'] font-bold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <span className="text-green-600 font-semibold text-sm tracking-wider uppercase mb-3 block">
              Our Journey
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-['Chivo'] font-bold text-gray-900 mb-4">
              Company Milestones
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 -translate-x-1/2" />

            <div className="space-y-8 md:space-y-0">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`relative md:flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  data-testid={`milestone-${index}`}
                >
                  {/* Content */}
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <div className="bg-gray-50 rounded-xl p-6 md:inline-block">
                      <span className="text-green-600 font-bold text-lg">{milestone.year}</span>
                      <h3 className="text-xl font-['Chivo'] font-bold text-gray-900 mt-1 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600 text-sm">{milestone.description}</p>
                    </div>
                  </div>

                  {/* Dot */}
                  <div className="hidden md:block absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-green-600 rounded-full border-4 border-white shadow" />

                  {/* Empty space for other side */}
                  <div className="md:w-1/2" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-green-600 font-semibold text-sm tracking-wider uppercase mb-3 block">
                Why Work With Us
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-['Chivo'] font-bold text-gray-900 mb-6">
                Your Success is Our Priority
              </h2>
              <p className="text-gray-600 text-base md:text-lg mb-8 leading-relaxed">
                We don't just fulfill orders – we partner with you to ensure your business thrives. 
                Our dedicated team, advanced technology, and commitment to excellence make us the 
                ideal fulfillment partner for growing ecommerce businesses.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  'Dedicated account manager for personalized support',
                  'Advanced technology for seamless integration',
                  'Flexible solutions that scale with your business',
                  'Transparent pricing with no hidden fees',
                  'Industry-leading accuracy and delivery rates'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                data-testid="about-cta-bottom"
                className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-700 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
              >
                Partner With Us
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.pexels.com/photos/4483608/pexels-photo-4483608.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Warehouse team at work"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Achievement badge */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-5 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Award size={24} className="text-green-600" />
                  </div>
                  <div>
                    <span className="font-bold text-gray-900 block">Top Rated</span>
                    <span className="text-sm text-gray-500">Fulfillment Partner</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-['Chivo'] font-bold text-white mb-6">
              Ready to Start Your{' '}
              <span className="text-green-500">Journey?</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg mb-10 leading-relaxed">
              Join thousands of successful sellers who trust Black Mesa Mercantile 
              for their fulfillment needs. Let's grow together.
            </p>
            <Link
              to="/contact"
              data-testid="about-final-cta"
              className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-green-700 transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 group"
            >
              Get in Touch
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
